import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Snackbar,
  Alert,
  Box,
  Avatar,
} from '@mui/material';
import { useUser } from '../../contexts/UserContext';

const THALI_OPTIONS = [
  { value: 'taking', label: 'Taking' },
  { value: 'not_taking', label: 'Not Taking' },
  { value: 'once_a_week', label: 'Once a Week' },
  { value: 'joint', label: 'Joint' },
  { value: 'other_centre', label: 'Other Centre' },
];

const ThaliStatusDialog = ({ open, onClose, row, onSave, year }) => {
  const { token } = useUser();
  const [thaliStatus, setThaliStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (row && open) {
      setThaliStatus(row.thali_status ?? '');
    }
  }, [row, open]);

  if (!row) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('https://api.fmb52.com/api/users/set-thali-status', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          family_id: String(row.family_id ?? ''),
          thali_status: thaliStatus || '',
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result?.success) {
        setSnackbar({ open: true, message: result?.message || 'Thali status updated successfully.', severity: 'success' });
        if (onSave) onSave(result?.data);
        onClose();
      } else {
        setSnackbar({ open: true, message: result?.message || 'Update failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An error occurred', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.04)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 3,
            fontWeight: 600,
            fontSize: '1.25rem',
            letterSpacing: '-0.01em',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.background.default,
          }}
        >
          Change Thali Status
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 3,
              py: 2.5,
              backgroundColor: '#fff',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Avatar
              src={row.photo?.file_url || ''}
              alt={row.name}
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: '1.1rem',
              }}
            >
              {row.name?.trim().charAt(0)?.toUpperCase() || '?'}
            </Avatar>
            <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ letterSpacing: '-0.01em' }}>
              {row.name}
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Select status
            </Typography>
            <RadioGroup
              row
              value={thaliStatus || ''}
              onChange={(e) => setThaliStatus(e.target.value)}
              sx={{
                flexWrap: 'wrap',
                gap: 1,
                '& .MuiFormControlLabel-root': { mr: 0 },
              }}
            >
              {THALI_OPTIONS.map(({ value, label }) => {
                const isSelected = (thaliStatus || '') === value;
                return (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio size="small" sx={{ display: 'none' }} />}
                    label={label}
                    labelPlacement="end"
                    sx={{
                      m: 0,
                      px: 2,
                      py: 1.25,
                      borderRadius: 2,
                      border: '1.5px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected ? 'primary.main' : 'transparent',
                      color: isSelected ? 'primary.contrastText' : 'text.primary',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: isSelected ? 'primary.dark' : 'action.hover',
                        backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                        color: isSelected ? 'primary.contrastText' : 'text.primary',
                      },
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.9rem',
                        fontWeight: isSelected ? 600 : 500,
                      },
                    }}
                  />
                );
              })}
            </RadioGroup>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1.5,
            backgroundColor: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.background.default,
          }}
        >
          <Button onClick={onClose} variant="outlined" color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, px: 2.5 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ThaliStatusDialog;
